import React, { useEffect, useState } from 'react';
import { tripAccessAPI, userAPI } from '../../../api/apiClient';
import useApi from '../../CustomHooks/useApi';
import { useToast } from '../../../Contexts/ToastContext';
import Loading from '../../../Loading';
import Select from 'react-select';
import ActionIcon from '../Participants/ActionIcon';
import { FaUserPlus, FaUserSlash, FaUser, FaSpinner, FaUserTie, FaUserMinus } from 'react-icons/fa';
import { useParams } from 'react-router-dom';
import ConfirmWithPhrasePopup from '../../Common/ConfirmWithPhrasePopup';
import '../../../css/View/TripAccessPanel.css';

const UserRoleEnum = {
  Coordinator: 2,
  Mentor: 5,
};

const AccessPanel = () => {
  const { callApi } = useApi();
  const { showToast, clearToast } = useToast();
  const { guid } = useParams();

  const [usersWithAccess, setUsersWithAccess] = useState([]);
  const [mentors, setMentors] = useState([]);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [expandedUserGuid, setExpandedUserGuid] = useState(null);

  const [isLoadingMentors, setIsLoadingMentors] = useState(false);
  const [isLoadingUsersWithAccess, setIsLoadingUsersWithAccess] = useState(false);

  const [showAddAccessPopup, setShowAddAccessPopup] = useState(false);
  const [showAddMentorPopup, setShowAddMentorPopup] = useState(false);
  const [selectedUserGuid, setSelectedUserGuid] = useState('');
  const [isAddingAccess, setIsAddingAccess] = useState(false);
  const [isAddingMentor, setIsAddingMentor] = useState(false);

  const [userToRemoveAccess, setUserToRemoveAccess] = useState(null);
  const [userToRemoveMentor, setUserToRemoveMentor] = useState(null);

  const fetchAllData = async () => {
    setIsLoadingMentors(true);
    setIsLoadingUsersWithAccess(true);
    try {
      const [usersResp, mentorsResp, availableResp] = await Promise.all([
        tripAccessAPI.getUsers(guid),
        tripAccessAPI.getMentors(guid),
        userAPI.getUsers(['Mentor', 'Coordinator']),
      ]);

      if (usersResp?.data) setUsersWithAccess(usersResp.data);
      if (mentorsResp?.data) setMentors(mentorsResp.data);
      if (availableResp?.data) setAvailableUsers(availableResp.data);
    } catch (error) {

    } finally {
      setIsLoadingMentors(false);
      setIsLoadingUsersWithAccess(false);
    }
  };

  useEffect(() => {
    fetchAllData();
  }, [guid]);

  const handleAddAccess = async () => {
    if (selectedUserGuid) {
      setShowAddAccessPopup(false);
      showToast('Przyznawanie dostępu...', 'processing');
      setIsAddingAccess(true);
      await callApi(
        () => tripAccessAPI.addUser(guid, selectedUserGuid),
        async () => {
          await fetchAllData();
          setIsAddingAccess(false);
          showToast('Dostęp został przyznany.', 'success');
          setSelectedUserGuid('');
        },
        async () => {
          setIsAddingAccess(false);
          clearToast();
        }
      );
    }
  };

  const handleAddMentor = async () => {
    if (selectedUserGuid) {
      setShowAddMentorPopup(false);
      showToast('Dodawanie osoby mentorskiej...', 'processing');
      setIsAddingMentor(true);
      await callApi(
        () => tripAccessAPI.addMentor(guid, selectedUserGuid),
        async () => {
          await fetchAllData();
          setIsAddingMentor(false);
          showToast('Osoba mentorska została dodana.', 'success');
          setSelectedUserGuid('');
        },
        async () => {
          setIsAddingMentor(false);
          clearToast();
        }
      );
    }
  };

  const handleRemoveAccessClick = (userGuid) => {
    setUserToRemoveAccess(userGuid);
  };

  const handleRemoveMentorClick = (userGuid) => {
    setUserToRemoveMentor(userGuid);
  };

  const confirmRemoveAccess = async () => {
    if (!userToRemoveAccess) return;
    showToast("Usuwanie uprawnień użytkownix ...", 'processing')
    await callApi(
      async () => tripAccessAPI.removeUser(guid, userToRemoveAccess), 
      async () => { setUserToRemoveAccess(null); fetchAllData(); showToast("Usunięto poprawnie uprawnienia do wycieczki!", 'success')},
      clearToast)

  };

  const confirmRemoveMentor = async () => {
    if (!userToRemoveMentor) return;
    showToast("Usuwanie osoby mentorskiej ...", 'processing')
    await callApi(
      async () => tripAccessAPI.removeMentor(guid, userToRemoveMentor), 
      async () => { setUserToRemoveMentor(null); fetchAllData(); showToast("Usunięto poprawnie osobę mentorską!", 'success')},
      clearToast)
  };

  const toggleExpanded = (userGuid) => {
    setExpandedUserGuid(expandedUserGuid === userGuid ? null : userGuid);
  };

  const formatDate = (isoDate) => {
    const date = new Date(isoDate);
    return `${date.getDate().toString().padStart(2, '0')}.${(date.getMonth() + 1)
      .toString()
      .padStart(2, '0')}.${date.getFullYear()}`;
  };

  const availableCoordinators = availableUsers
    .filter(user =>
      user.isActive &&
      user.roles.some(role => role.role === UserRoleEnum.Coordinator) &&
      !usersWithAccess.some(u => u.guid === user.guid)
    )
    .sort((a, b) => a.name.localeCompare(b.name));

  const availableMentors = availableUsers
    .filter(user =>
      user.isActive &&
      user.roles.some(role => role.role === UserRoleEnum.Mentor) &&
      !mentors.some(u => u.guid === user.guid)
    )
    .sort((a, b) => a.name.localeCompare(b.name));

  const availableList = showAddAccessPopup ? availableCoordinators : availableMentors;

  return (
    <div className="tripaccess-container">
      <div className="tripaccess-header">
        <div className="tripaccess-actions">
          <ActionIcon
            icon={isAddingAccess ? <FaSpinner className="tripaccess-spinner" /> : <FaUserPlus />}
            label="Przyznaj dostęp"
            onClick={() => setShowAddAccessPopup(true)}
          />
          <ActionIcon
            icon={isAddingMentor ? <FaSpinner className="tripaccess-spinner" /> : <FaUserTie />}
            label="Dodaj os. mentorską"
            onClick={() => setShowAddMentorPopup(true)}
          />
        </div>
      </div>

      <div className="tripaccess-userlist">
        <div className="tripaccess-role-group">
          <h3 className="tripaccess-role-title">Osoby mentorskie</h3>
          {isLoadingMentors ? (
            <Loading text="Ładowanie mentorów..." />
          ) : mentors.length === 0 ? (
            <div className="tripaccess-empty">-- Brak --</div>
          ) : (
            mentors.map((user) => (
              <div key={user.guid} className="tripaccess-user-card" onClick={() => toggleExpanded(user.guid)}>
                <div className="tripaccess-user-main">
                  <div className="tripaccess-user-info">
                    <span className="tripaccess-user-name">{user.name}</span>
                    <span className="tripaccess-user-email">{user.email}</span>
                  </div>
                  <div className="tripaccess-user-actions">
                    <div className="tripaccess-action-icon" onClick={(e) => { e.stopPropagation(); handleRemoveMentorClick(user.guid); }}>
                      <FaUserSlash />
                    </div>
                  </div>
                </div>
                {expandedUserGuid === user.guid && (
                  <div className="tripaccess-user-expanded">
                    Dodano: {formatDate(user.grantedAt)} przez {user.grantedBy}
                  </div>
                )}
              </div>
            ))
          )}
        </div>

        {/* Access */}
        <div className="tripaccess-role-group">
          <h3 className="tripaccess-role-title">Osoby z dostępem</h3>
          {isLoadingUsersWithAccess ? (
            <Loading text="Ładowanie dostępu..." />
          ) : usersWithAccess.length === 0 ? (
            <div className="tripaccess-empty">-- Brak --</div>
          ) : (
            usersWithAccess.map((user) => (
              <div key={user.guid} className="tripaccess-user-card" onClick={() => toggleExpanded(user.guid)}>
                <div className="tripaccess-user-main">
                  <div className="tripaccess-user-info">
                    <span className="tripaccess-user-name">{user.name}</span>
                    <span className="tripaccess-user-email">{user.email}</span>
                  </div>
                  <div className="tripaccess-user-actions">
                    <div className="tripaccess-action-icon" onClick={(e) => { e.stopPropagation(); handleRemoveAccessClick(user.guid); }}>
                      <FaUserSlash />
                    </div>
                  </div>
                </div>
                {expandedUserGuid === user.guid && (
                  <div className="tripaccess-user-expanded">
                    Dodano: {formatDate(user.grantedAt)} przez {user.grantedBy}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>

      {userToRemoveAccess && (
        <ConfirmWithPhrasePopup
          text={
            <>
              Czy na pewno chcesz usunąć dostęp osobie{" "}
              <span style={{ fontWeight: "bold", alignItems: "center", gap: "6px" }}>
                <FaUser /> {usersWithAccess.find(u => u.guid === userToRemoveAccess)?.name}
              </span>
              {" "}do tej wycieczki?
            </>
          }
          phrase="okoń"
          onConfirm={confirmRemoveAccess}
          onCancel={() => setUserToRemoveAccess(null)}
        />
      )}
      {userToRemoveMentor && (
        <ConfirmWithPhrasePopup
          text={
            <>
              Czy na pewno chcesz usunąć osobę mentorską{" "}
              <span style={{ fontWeight: "bold", alignItems: "center", gap: "6px" }}>
                <FaUser /> {mentors.find(u => u.guid === userToRemoveMentor)?.name}
              </span>
              {" "}z tej wycieczki?
            </>
          }
          phrase="okoń"
          onConfirm={confirmRemoveMentor}
          onCancel={() => setUserToRemoveMentor(null)}
        />
      )}

{showAddAccessPopup && (
  <div className="tripaccess-popup-overlay">
    <div className="tripaccess-popup fade-in">
      <div className="tripaccess-popup-header">
        <h3>Przyznaj dostęp</h3>
      </div>
      <div className="tripaccess-popup-body">
        <Select
          options={availableCoordinators.map(user => ({
            value: user.guid,
            label: (
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <span>{user.name}</span>
                <small style={{ color: 'gray' }}>{user.email}</small>
              </div>
            ),
            searchText: `${user.name} ${user.email}`.toLowerCase()
          }))}
          onChange={(selectedOption) => setSelectedUserGuid(selectedOption?.value || '')}
          placeholder="Wyszukaj osobę..."
          noOptionsMessage={() => "Brak dostępnych użytkowników"}
          isClearable
          filterOption={(option, inputValue) => {
            return option.data.searchText.includes(inputValue.toLowerCase());
          }}
        />
      </div>
      <div className="tripaccess-popup-footer">
        <button
          className="tripaccess-cancel-button"
          onClick={() => {
            setShowAddAccessPopup(false);
            setSelectedUserGuid('');
          }}
        >
          Anuluj
        </button>
        <button
          className="tripaccess-submit-button"
          onClick={handleAddAccess}
          disabled={!selectedUserGuid}
        >
          Dodaj
        </button>
      </div>
    </div>
  </div>
)}

{showAddMentorPopup && (
  <div className="tripaccess-popup-overlay">
    <div className="tripaccess-popup fade-in">
      <div className="tripaccess-popup-header">
        <h3>Dodaj osobę mentorską</h3>
      </div>
      <div className="tripaccess-popup-body">
        <Select
          options={availableMentors.map(user => ({
            value: user.guid,
            label: (
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <span>{user.name}</span>
                <small style={{ color: 'gray' }}>{user.email}</small>
              </div>
            ),
            searchText: `${user.name} ${user.email}`.toLowerCase()
          }))}
          onChange={(selectedOption) => setSelectedUserGuid(selectedOption?.value || '')}
          placeholder="Wyszukaj osobę..."
          noOptionsMessage={() => "Brak dostępnych użytkowników"}
          isClearable
          filterOption={(option, inputValue) => {
            return option.data.searchText.includes(inputValue.toLowerCase());
          }}
        />
      </div>
      <div className="tripaccess-popup-footer">
      <button
          className="tripaccess-cancel-button"
          onClick={() => {
            setShowAddMentorPopup(false);
            setSelectedUserGuid('');
          }}
        >
          Anuluj
        </button>
        <button
          className="tripaccess-submit-button"
          onClick={handleAddMentor}
          disabled={!selectedUserGuid}
        >
          Dodaj
        </button>

      </div>
    </div>
  </div>
)}

    </div>
  );
};

export default AccessPanel;
